'use client';
import React, { forwardRef, useCallback, useEffect, useRef, useState, version, } from 'react';
import { encode } from 'universal-base64';
import { useInView } from './useInView.js';
const isSsr = typeof window === 'undefined';
const isIntersectionObserverAvailable = isSsr
    ? false
    : !!window.IntersectionObserver;
function fetchPriorityProp(fetchPriority) {
    const [majorStr, minorStr] = version.split('.');
    const major = parseInt(majorStr, 10);
    const minor = parseInt(minorStr, 10);
    if (major > 18 || (major === 18 && minor >= 3)) {
        // In React 18.3.0 or newer, we must use camelCase
        // prop to avoid "Warning: Invalid DOM property".
        // See https://github.com/facebook/react/pull/25927
        return { fetchPriority };
    }
    // In React 18.2.0 or older, we must use lowercase prop
    // to avoid "Warning: Invalid DOM property".
    return { fetchpriority: fetchPriority };
}
const imageAddStrategy = ({ lazyLoad, inView, loaded }) => {
    if (!lazyLoad) {
        return true;
    }
    if (isSsr) {
        return false;
    }
    if (isIntersectionObserverAvailable) {
        return inView || loaded;
    }
    return true;
};
const imageShowStrategy = ({ lazyLoad, loaded }) => {
    if (!lazyLoad) {
        return true;
    }
    if (isSsr) {
        return false;
    }
    if (isIntersectionObserverAvailable) {
        return loaded;
    }
    return true;
};
const bogusBaseUrl = 'https://example.com/';
const buildSrcSet = (src, width, candidateMultipliers) => {
    if (!(src && width)) {
        return undefined;
    }
    return candidateMultipliers
        .map((multiplier) => {
        const url = new URL(src, bogusBaseUrl);
        if (multiplier !== 1) {
            url.searchParams.set('dpr', `${multiplier}`);
            const maxH = url.searchParams.get('max-h');
            const maxW = url.searchParams.get('max-w');
            if (maxH) {
                url.searchParams.set('max-h', `${Math.floor(parseInt(maxH) * multiplier)}`);
            }
            if (maxW) {
                url.searchParams.set('max-w', `${Math.floor(parseInt(maxW) * multiplier)}`);
            }
        }
        const finalWidth = Math.floor(width * multiplier);
        if (finalWidth < 50) {
            return null;
        }
        return `${url.toString().replace(bogusBaseUrl, '/')} ${finalWidth}w`;
    })
        .filter(Boolean)
        .join(',');
};
export const Image = forwardRef(({ className, fadeInDuration = 500, intersectionTreshold, intersectionThreshold, intersectionMargin, pictureClassName, lazyLoad: rawLazyLoad = true, style, pictureStyle, layout = 'intrinsic', objectFit, objectPosition, data, onLoad, usePlaceholder = true, priority = false, sizes, srcSetCandidates = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4], placeholderClassName, placeholderStyle, }, ref) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const lazyLoad = priority ? false : rawLazyLoad;
    const [loaded, setLoaded] = useState(false);
    const imageRef = useRef(null);
    const handleLoad = () => {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad();
        setLoaded(true);
    };
    // https://stackoverflow.com/q/39777833/266535
    useEffect(() => {
        if (!imageRef.current) {
            return;
        }
        if (imageRef.current.complete && imageRef.current.naturalWidth) {
            handleLoad();
        }
    }, []);
    const [viewRef, inView] = useInView({
        threshold: intersectionThreshold || intersectionTreshold || 0,
        rootMargin: intersectionMargin || '0px 0px 0px 0px',
        triggerOnce: true,
        fallbackInView: true,
    });
    const callbackRef = useCallback((_ref) => {
        viewRef(_ref);
        if (ref) {
            ref.current = _ref;
        }
    }, [viewRef]);
    const absolutePositioning = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        maxWidth: 'none',
        maxHeight: 'none',
    };
    const addImage = imageAddStrategy({
        lazyLoad,
        inView,
        loaded,
    });
    const showImage = imageShowStrategy({
        lazyLoad,
        inView,
        loaded,
    });
    const webpSource = data.webpSrcSet && (React.createElement("source", { srcSet: data.webpSrcSet, sizes: (_a = sizes !== null && sizes !== void 0 ? sizes : data.sizes) !== null && _a !== void 0 ? _a : undefined, type: "image/webp" }));
    const regularSource = (React.createElement("source", { srcSet: (_b = data.srcSet) !== null && _b !== void 0 ? _b : buildSrcSet(data.src, data.width, srcSetCandidates), sizes: (_c = sizes !== null && sizes !== void 0 ? sizes : data.sizes) !== null && _c !== void 0 ? _c : undefined }));
    const transition = fadeInDuration > 0 ? `opacity ${fadeInDuration}ms` : undefined;
    const placeholder = usePlaceholder && (data.bgColor || data.base64) ? (React.createElement("img", { "aria-hidden": "true", alt: "", src: (_d = data.base64) !== null && _d !== void 0 ? _d : undefined, className: placeholderClassName, style: Object.assign({ backgroundColor: (_e = data.bgColor) !== null && _e !== void 0 ? _e : undefined, objectFit,
            objectPosition,
            transition, opacity: showImage ? 0 : 1, 
            // During the opacity transition of the placeholder to the definitive version,
            // hardware acceleration is triggered. This results in the browser trying to render the
            // placeholder with your GPU, causing blurred edges. Solution: style the placeholder
            // so the edges overflow the container
            position: 'absolute', left: '-5%', top: '-5%', width: '110%', height: '110%', maxWidth: 'none', maxHeight: 'none' }, placeholderStyle) })) : null;
    const { width, aspectRatio } = data;
    const height = (_f = data.height) !== null && _f !== void 0 ? _f : (aspectRatio ? width / aspectRatio : 0);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"></svg>`;
    const sizer = layout !== 'fill' ? (React.createElement("img", { className: pictureClassName, style: Object.assign({ display: 'block', width: '100%' }, pictureStyle), src: `data:image/svg+xml;base64,${encode(svg)}`, "aria-hidden": "true", alt: "" })) : null;
    return (React.createElement("div", { ref: callbackRef, className: className, style: Object.assign(Object.assign({ overflow: 'hidden' }, (layout === 'fill'
            ? absolutePositioning
            : layout === 'intrinsic'
                ? { position: 'relative', width: '100%', maxWidth: width }
                : layout === 'fixed'
                    ? { position: 'relative', width }
                    : { position: 'relative', width: '100%' })), style) },
        sizer,
        placeholder,
        addImage && (React.createElement("picture", null,
            webpSource,
            regularSource,
            data.src && (
            // biome-ignore lint/a11y/useAltText: We do support the `alt` attribute
            React.createElement("img", Object.assign({ ref: imageRef, src: data.src, alt: (_g = data.alt) !== null && _g !== void 0 ? _g : '', title: (_h = data.title) !== null && _h !== void 0 ? _h : undefined, onLoad: handleLoad }, fetchPriorityProp(priority ? 'high' : undefined), { className: pictureClassName, style: Object.assign(Object.assign(Object.assign({ opacity: showImage ? 1 : 0, transition }, absolutePositioning), { objectFit,
                    objectPosition }), pictureStyle) }))))),
        React.createElement("noscript", null,
            React.createElement("picture", null,
                webpSource,
                regularSource,
                data.src && (
                // biome-ignore lint/a11y/useAltText: We do support the `alt` attribute
                React.createElement("img", Object.assign({ src: data.src, alt: (_j = data.alt) !== null && _j !== void 0 ? _j : '', title: (_k = data.title) !== null && _k !== void 0 ? _k : undefined, className: pictureClassName, style: Object.assign(Object.assign(Object.assign({}, absolutePositioning), { objectFit,
                        objectPosition }), pictureStyle), loading: lazyLoad ? 'lazy' : undefined }, fetchPriorityProp(priority ? 'high' : undefined))))))));
});
//# sourceMappingURL=index.js.map